import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import moment from 'moment';
import BottomSheet from '../shared/molecules/BottomSheet';
import Box from '../shared/atoms/Box';
import Text from '../shared/atoms/Text';
import Badge from '../shared/atoms/Badge';
import { yieldBonusType } from '../../utils/slots';

const Section = styled.div`
  margin-bottom: 24px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 12px;
  border-bottom: 1px solid #f0f0f0;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
`;

const HeaderTitle = styled.h3`
  font-family: 'LTSaeada', Arial, sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const ToggleButton = styled.button`
  background: ${(props) => (props.active ? '#5b5ff5' : 'white')};
  color: ${(props) => (props.active ? 'white' : '#5b5ff5')};
  border: 2px solid #5b5ff5;
  border-radius: 8px;
  padding: 6px 12px;
  font-family: 'LTSaeada', Arial, sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:active {
    transform: scale(0.95);
  }
`;

const SectionTitle = styled.div`
  font-family: 'LTSaeada', Arial, sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
`;

const SlotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;

const SlotButton = styled.button`
  padding: 16px 12px;
  background: ${(props) => {
    if (props.disabled) return '#f3f4f6';
    if (props.selected) return '#5b5ff5';
    return 'white';
  }};
  color: ${(props) => {
    if (props.disabled) return '#9ca3af';
    if (props.selected) return 'white';
    return '#1f2937';
  }};
  border: 2px solid
    ${(props) => {
      if (props.disabled) return '#e5e7eb';
      if (props.selected) return '#5b5ff5';
      return '#e5e7eb';
    }};
  border-radius: 12px;
  font-family: 'LTSaeada', Arial, sans-serif;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  position: relative;

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  ${(props) =>
    !props.disabled &&
    !props.selected &&
    `
    &:hover {
      border-color: #5b5ff5;
      background: #f9fafb;
    }
  `}
`;

const BonusBadge = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  background: #d5ff4f;
  color: #1f2937;
  font-size: 0.625rem;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 6px;
  text-transform: lowercase;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px 16px;
  color: #9ca3af;
  font-family: 'LTSaeada', Arial, sans-serif;
`;

const DaySlotBottomSheet = ({
  open,
  onClose,
  day,
  slots,
  selectedSlots,
  onToggleSlot,
  zone,
  trackFunnelStep = () => {},
}) => {
  // FIX-579: hypothesis is that on mobile the sheet (z-index 9999, 80% of the
  // screen, no validate button of its own) covers the fixed "Continuer"
  // button underneath (z-index 1000) — opened/closed pairs let that be
  // checked against how often the client actually reaches submit afterwards.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      trackFunnelStep('slot_sheet_opened', { zone_id: zone?.id });
    } else if (!open && wasOpen.current) {
      trackFunnelStep('slot_sheet_closed', { zone_id: zone?.id });
    }
    wasOpen.current = open;
  }, [open]);

  if (!day || !slots) return null;

  const dayMoment = moment(day.date);
  const title = dayMoment.format('dddd DD MMMM');

  // Séparer AM et PM
  const amSlots = slots.filter((slot) => slot.min < 14);
  const pmSlots = slots.filter((slot) => slot.min >= 14);

  const isSlotSelected = (slot) => {
    return selectedSlots.some(
      (s) =>
        s.available_min ===
          dayMoment.set({ hour: slot.min }).format('YYYY-MM-DD HH:00:00') &&
        s.available_max ===
          dayMoment.set({ hour: slot.max }).format('YYYY-MM-DD HH:00:00'),
    );
  };

  const handleSlotClick = (slot) => {
    if (slot.full) return;
    const available_min = dayMoment
      .set({ hour: slot.min })
      .format('YYYY-MM-DD HH:00:00');
    const available_max = dayMoment
      .set({ hour: slot.max })
      .format('YYYY-MM-DD HH:00:00');
    onToggleSlot(available_min, available_max);
  };

  const getBonusLabel = (bonusType, yieldAmount) => {
    if (!bonusType) return null;
    const price = yieldAmount ? `${yieldAmount}€` : '';
    return `+${bonusType}${price ? ` (${price})` : ''}`;
  };

  const renderSlot = (slot) => {
    const selected = isSlotSelected(slot);
    const bonusType =
      zone?.id === 1
        ? yieldBonusType(
            dayMoment.set({ hour: slot.min }).format('YYYY-MM-DD HH:00:00'),
          )
        : null;
    const bonusLabel = getBonusLabel(bonusType, slot.yieldAmount);

    return (
      <SlotButton
        key={`${slot.min}-${slot.max}`}
        disabled={slot.full}
        selected={selected}
        onClick={() => handleSlotClick(slot)}>
        {bonusLabel && <BonusBadge>{bonusLabel}</BonusBadge>}
        {String(slot.min).padStart(2, '0')}:00-
        {String(slot.max).padStart(2, '0')}:00
      </SlotButton>
    );
  };

  const handleToggleAll = () => {
    const availableSlots = slots.filter((s) => !s.full);
    const allSelected = availableSlots.every((s) => isSlotSelected(s));

    if (allSelected) {
      // Désélectionner tous les créneaux de ce jour
      availableSlots.forEach((slot) => {
        if (isSlotSelected(slot)) {
          handleSlotClick(slot);
        }
      });
    } else {
      // Sélectionner tous les créneaux disponibles
      availableSlots.forEach((slot) => {
        if (!isSlotSelected(slot)) {
          handleSlotClick(slot);
        }
      });
    }
  };

  const availableSlots = slots.filter((s) => !s.full);
  const allSelected =
    availableSlots.length > 0 && availableSlots.every((s) => isSlotSelected(s));

  return (
    <BottomSheet open={open} onClose={onClose}>
      <HeaderActions>
        <HeaderTitle>{title}</HeaderTitle>
        <ToggleButton active={allSelected} onClick={handleToggleAll}>
          {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
        </ToggleButton>
      </HeaderActions>
      <Box p={3}>
        {amSlots.length > 0 && (
          <Section>
            <SectionTitle>Matin</SectionTitle>
            <SlotGrid>{amSlots.map(renderSlot)}</SlotGrid>
          </Section>
        )}

        {pmSlots.length > 0 && (
          <Section>
            <SectionTitle>Après-midi</SectionTitle>
            <SlotGrid>{pmSlots.map(renderSlot)}</SlotGrid>
          </Section>
        )}

        {amSlots.length === 0 && pmSlots.length === 0 && (
          <EmptyState>Aucun créneau disponible pour ce jour</EmptyState>
        )}
      </Box>
    </BottomSheet>
  );
};

DaySlotBottomSheet.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  day: PropTypes.object,
  slots: PropTypes.array,
  selectedSlots: PropTypes.array.isRequired,
  onToggleSlot: PropTypes.func.isRequired,
  zone: PropTypes.object,
  trackFunnelStep: PropTypes.func,
};

export default DaySlotBottomSheet;
